import React, { useState, useEffect } from 'react';
import { getUnifiedDramaAnalysis, reanalyzeDrama, getAvailableWeeks, getAvailableSeasons } from '../../adminApi';
import styles from './DramaAnalysis.module.css';

const DramaAnalysis = () => {
  const [dramaData, setDramaData] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [weeksResponse, seasonsResponse] = await Promise.all([
        getAvailableWeeks(),
        getAvailableSeasons()
      ]);

      setWeeks(weeksResponse.data || []);
      setSeasons(seasonsResponse.data || []);

      // Set default to current season
      if (seasonsResponse.data && seasonsResponse.data.length > 0) {
        setSelectedSeason(seasonsResponse.data[0]);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setMessage({ type: 'error', text: 'Failed to load weeks and seasons' });
    }
  };

  const loadDramaAnalysis = async () => {
    if (!selectedWeek || !selectedSeason) {
      setMessage({ type: 'error', text: 'Please select both week and season' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const response = await getUnifiedDramaAnalysis(selectedWeek, selectedSeason);
      setDramaData(response.data || []);

      if (response.data && response.data.length === 0) {
        setMessage({ type: 'info', text: 'No locks found for this week' });
      } else {
        const completedGames = response.data.filter(pick => pick.completed).length;
        const totalGames = response.data.length;
        setMessage({
          type: 'info',
          text: `Found ${totalGames} locks for Week ${selectedWeek}. ${completedGames} games completed, ${totalGames - completedGames} pending.`
        });
      }
    } catch (error) {
      console.error('Error loading drama analysis:', error);
      setMessage({ type: 'error', text: 'Failed to load drama analysis' });
    } finally {
      setLoading(false);
    }
  };

  const handleReanalyze = async () => {
    if (!selectedWeek || !selectedSeason) {
      setMessage({ type: 'error', text: 'Please select both week and season' });
      return;
    }

    if (!window.confirm(`Are you sure you want to re-analyze drama for Week ${selectedWeek}? This will fetch fresh drives data from the API and may take a few minutes.`)) {
      return;
    }

    setLoading(true);
    setMessage({ type: 'info', text: 'Re-analyzing drama... This may take a few minutes.' });

    try {
      const response = await reanalyzeDrama(selectedWeek, selectedSeason);

      if (response.data.success) {
        const results = response.data.results;
        setMessage({
          type: 'success',
          text: `Re-analysis completed! Analyzed ${results.analyzed}/${results.totalPicks} picks. Found ${results.closeCallsFound} close calls, ${results.ouDramaFound} O/U drama, ${results.atsDramaFound} ATS drama.`
        });

        // Reload the data to show updated results
        await loadDramaAnalysis();
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Re-analysis failed' });
      }
    } catch (error) {
      console.error('Error re-analyzing drama:', error);
      setMessage({ type: 'error', text: 'Failed to re-analyze drama' });
    } finally {
      setLoading(false);
    }
  };


  const formatMargin = (margin) => {
    if (margin === null || margin === undefined) return 'N/A';
    return margin > 0 ? `+${margin}` : margin.toString();
  };

  const getPickDisplay = (pick) => {
    if (pick.pick_type === 'home' || pick.pick_type === 'away') {
      const teamAbbr = pick.pick_type === 'home' ? pick.home_team_abbr : pick.away_team_abbr;
      return `${teamAbbr} ${pick.lock_line}`;
    } else if (pick.pick_type === 'over') {
      return `Over ${pick.lock_line}`;
    } else if (pick.pick_type === 'under') {
      return `Under ${pick.lock_line}`;
    }
    return 'Unknown';
  };

  const getResultIcon = (wonLost) => {
    if (wonLost === 'won') return '✅';
    if (wonLost === 'lost') return '❌';
    if (wonLost === 'push') return '➖';
    return '❓';
  };


  const getDramaTypeColor = (type) => {
    if (type === 'comeback') return '#4caf50'; // Green
    if (type === 'collapse') return '#f44336'; // Red
    if (type === 'close_call') return '#ff9800'; // Orange
    if (type === 'none') return '#9e9e9e'; // Gray
    if (type === '-') return '#e0e0e0'; // Light gray for incomplete
    return '#9e9e9e'; // Default gray
  };

  const formatGameStatus = (completed, startDate) => {
    if (completed) return '✅ Completed';
    const gameDate = new Date(startDate);
    const now = new Date();
    if (gameDate > now) return '⏰ Future';
    return '🔄 In Progress';
  };

  return (
    <div className={styles.container}>
      <h2>Drama Analysis</h2>

      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label htmlFor="season">Season:</label>
          <select
            id="season"
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className={styles.select}
          >
            <option value="">Select Season</option>
            {seasons.filter(season => season != null).map((season, index) => (
              <option key={`season-${season || index}`} value={season}>
                {season}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="week">Week:</label>
          <select
            id="week"
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(e.target.value)}
            className={styles.select}
          >
            <option value="">Select Week</option>
            {weeks.filter(week => week != null).map((week, index) => (
              <option key={`week-${week || index}`} value={week}>
                Week {week}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.buttonGroup}>
          <button
            onClick={loadDramaAnalysis}
            disabled={loading || !selectedWeek || !selectedSeason}
            className={styles.loadButton}
          >
            {loading ? 'Loading...' : 'Load Analysis'}
          </button>

          <button
            onClick={handleReanalyze}
            disabled={loading || !selectedWeek || !selectedSeason}
            className={styles.reanalyzeButton}
          >
            {loading ? 'Processing...' : 'Re-analyze Week'}
          </button>
        </div>
      </div>

      {/* Drama Data Table */}
      {dramaData.length > 0 && (
        <div className={styles.tableContainer}>
          <h3>Unified Drama Analysis Results</h3>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Pick</th>
                  <th>Game Status</th>
                  <th>Result</th>
                  <th>Margin</th>
                  <th>Close Call</th>
                  <th>O/U Drama</th>
                  <th>ATS Drama</th>
                  <th>Game Score</th>
                </tr>
              </thead>
              <tbody>
                {dramaData.map((pick, index) => {
                  return (
                    <tr key={`drama-${pick.id || index}`} className={styles.tableRow}>
                      <td className={styles.userCell}>
                        <strong>{pick.username}</strong>
                      </td>
                      <td className={styles.pickCell}>
                        {getPickDisplay(pick)}
                      </td>
                      <td className={styles.statusCell}>
                        {formatGameStatus(pick.completed, pick.start_date)}
                      </td>
                      <td className={styles.resultCell}>
                        <span className={styles.resultIcon}>
                          {getResultIcon(pick.won_lost)}
                        </span>
                        <span className={styles.resultText}>
                          {pick.won_lost || 'N/A'}
                        </span>
                      </td>
                      <td className={styles.marginCell}>
                        {formatMargin(pick.margin)}
                      </td>
                      <td className={styles.dramaCell}>
                        <span
                          className={styles.dramaType}
                          style={{ color: getDramaTypeColor(pick.close_call) }}
                        >
                          {pick.close_call}
                        </span>
                        {pick.close_call !== '-' && pick.close_call !== 'none' && (
                          <div className={styles.dramaDetails}>
                            {pick.close_call_details}
                          </div>
                        )}
                      </td>
                      <td className={styles.dramaCell}>
                        <span
                          className={styles.dramaType}
                          style={{ color: getDramaTypeColor(pick.ou_drama) }}
                        >
                          {pick.ou_drama}
                        </span>
                        {pick.ou_drama !== '-' && pick.ou_drama !== 'none' && pick.ou_drama !== 'N/A' && (
                          <div className={styles.dramaDetails}>
                            {pick.ou_drama_details}
                          </div>
                        )}
                      </td>
                      <td className={styles.dramaCell}>
                        <span
                          className={styles.dramaType}
                          style={{ color: getDramaTypeColor(pick.ats_drama) }}
                        >
                          {pick.ats_drama}
                        </span>
                        {pick.ats_drama !== '-' && pick.ats_drama !== 'none' && pick.ats_drama !== 'N/A' && (
                          <div className={styles.dramaDetails}>
                            {pick.ats_drama_details}
                          </div>
                        )}
                      </td>
                      <td className={styles.gameCell}>
                        {pick.home_team_abbr} {pick.home_points || '-'} - {pick.away_team_abbr} {pick.away_points || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className={styles.helpSection}>
        <h3>Understanding Unified Drama Analysis</h3>
        <div className={styles.helpContent}>
          <div className={styles.helpItem}>
            <span className={styles.helpLabel}>Close Call:</span>
            <span className={styles.helpText}>Final margin within 1 point of spread (all pick types)</span>
          </div>
          <div className={styles.helpItem}>
            <span className={styles.helpLabel}>O/U Drama:</span>
            <span className={styles.helpText}>Over/Under picks with dramatic finishes (50% threshold criteria)</span>
          </div>
          <div className={styles.helpItem}>
            <span className={styles.helpLabel}>ATS Drama:</span>
            <span className={styles.helpText}>Home/Away picks with comeback/collapse scenarios (Optimum Schedule criteria)</span>
          </div>
          <div className={styles.helpItem}>
            <span className={styles.helpLabel}>Comeback:</span>
            <span className={styles.helpText}>Started behind schedule, spent >50% out of cover zone, entered cover zone in 2nd half</span>
          </div>
          <div className={styles.helpItem}>
            <span className={styles.helpLabel}>Collapse:</span>
            <span className={styles.helpText}>Started ahead of schedule, spent >50% in cover zone, left cover zone in 2nd half</span>
          </div>
          <div className={styles.helpItem}>
            <span className={styles.helpLabel}>Game Status:</span>
            <span className={styles.helpText}>✅ Completed, 🔄 In Progress, ⏰ Future (shows "-" for incomplete games)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DramaAnalysis;
